import {
  DataProvider,
  Filtering,
  QDataTable,
  QDataTableActionBar,
  QText,
  useCurrentUser,
} from '@qualio/ui-components';
import { useContext } from 'react';
import { QueryClient } from 'react-query';
import { documentApi } from '../../../../../../api/document';
import { QualioDocument } from '../../../../../../api/model/document';
import { BulkActionsWrapper } from '../../BulkActionsV2/BulkActionWrapper';
import { getTableParams, TableFilter } from '../../Filter/TableFilters';
import { useDeleteDraftSelected } from '../../hooks/useDeleteDraftSelected';
import { useWorkspaceTableColumns } from '../../hooks/useWorkspaceTableColumns';
import { WorkspaceTabsContext } from '../../WorkspaceTabsContext';
import { WorkspaceTabs } from '../../WorkspaceV2';

const buildQueryKey = (companyId?: number) => [
  'all-documents',
  Number(companyId),
];

export const invalidateAllDocumentsTableData = async (
  queryClient: QueryClient,
  companyId: number,
) => {
  await queryClient.invalidateQueries(buildQueryKey(companyId));
};

export const AllDocumentsTable = () => {
  const currentUser = useCurrentUser();
  const deletedDraftStatusSelected = useDeleteDraftSelected();
  const { setIsReadyForExport } = useContext(WorkspaceTabsContext);

  const getAllDocuments = async ({
    pageParam,
  }: {
    pageParam: DataProvider.PageParams;
  }): Promise<DataProvider.TDataWithCount<QualioDocument>> => {
    setIsReadyForExport(false);
    const {
      offset,
      limit,
      orderBy,
      searchTerm,
      statusFilters,
      templateFilter,
      tagsFilter,
      ownerFilter,
      accountFilter,
    } = getTableParams(pageParam);

    const { total: itemCount, documents: data } = await documentApi.fetchAll(
      currentUser,
      statusFilters,
      searchTerm,
      tagsFilter,
      offset * limit,
      limit,
      orderBy,
      templateFilter,
      ownerFilter,
      accountFilter,
    );

    if (itemCount > 0 && data?.length > 0) {
      setIsReadyForExport(true);
    }

    return {
      itemCount,
      data: data || [],
    };
  };

  const columns = useWorkspaceTableColumns();

  return (
    <DataProvider.Remote
      queryKey={buildQueryKey(currentUser.companyId)}
      queryFn={getAllDocuments}
      queryParamKeys={{
        searchTerm: 'query',
        pageIndex: 'offset',
        pageSize: 'limit',
      }}
    >
      {deletedDraftStatusSelected && (
        <QText fontSize="sm">
          Deleted items will remain in this view for 90 days
        </QText>
      )}
      <QDataTable
        data-cy="workspace-all-documents-table"
        columns={columns}
        getRowId={(row) => row.id.toString()}
      >
        <Filtering.FormContent>
          <TableFilter tab={WorkspaceTabs.ALL_DOCUMENTS} />
        </Filtering.FormContent>
        {!deletedDraftStatusSelected && (
          <QDataTableActionBar>
            <BulkActionsWrapper currentTab={WorkspaceTabs.ALL_DOCUMENTS} />
          </QDataTableActionBar>
        )}
      </QDataTable>
    </DataProvider.Remote>
  );
};
